import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useGetClientUserQuery, useResetSignatureMutation } from '../../api/client-users-api';
import { clientUsersAlertId, clientUserSignatureResetModalId } from '../../constants/constants';
import { selectActiveClientUserId, setActiveClientUserId } from '../../store/client-users-slice';

const ClientUserSignatureResetModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['client-users', 'common']);
  const dispatch = useDispatch();
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();
  const activeClientUserId = useAppSelector(selectActiveClientUserId);
  const [resetSignature, { isLoading: isLoadingResetSignature }] = useResetSignatureMutation();

  const { data: clientUserData } = useGetClientUserQuery(activeClientUserId || '', {
    skip: !activeClientUserId,
  });

  const closeClientUserSignatureResetModal = () => {
    dispatch(closeModal(clientUserSignatureResetModalId));
    dispatch(setActiveClientUserId(null));
  };

  const confirmReset = () => {
    if (!activeClientUserId) {
      return;
    }
    resetConfirmationError();

    resetSignature(activeClientUserId)
      .unwrap()
      .then(() => {
        dispatch(
          setAlert({
            id: clientUsersAlertId,
            type: 'success',
            title: (
              <Trans
                i18nKey="clientUserSignatureResetModal.message.successSignatureReset"
                t={t}
                ns="client-users"
                values={{ name: clientUserData?.name || '' }}
              />
            ),
          }),
        );
        closeClientUserSignatureResetModal();
      })
      .catch(() => addConfirmationError(t('errors.unexpected', { ns: 'common' })));
  };

  return (
    <Modal
      title={t('clientUserSignatureResetModal.signatureReset')}
      onCancel={closeClientUserSignatureResetModal}
      isLoading={isLoadingResetSignature}
      customSubmitText={t('action.signatureReset')}
      customDeleteText={t('action.cancel', { ns: 'common' })}
      onOk={confirmReset}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      {clientUserData && (
        <ModalConfirmationContent
          confirmDeleteText={
            <Trans
              i18nKey="clientUserSignatureResetModal.confirmationSignatureReset"
              ns="client-users"
              t={t}
              values={{ name: clientUserData?.name || '' }}
            />
          }
          confirmationError={confirmationError}
        />
      )}
    </Modal>
  );
};

const ClientUserSignatureResetModal = () => {
  return <LazyModal modalId={clientUserSignatureResetModalId} Component={ClientUserSignatureResetModalContent} />;
};

export default ClientUserSignatureResetModal;
